import React, { Component } from 'react';
import moment from 'moment';
import {
  Button,
  Checkbox,
  Glyphicon,
  OverlayTrigger,
  Popover,
} from 'react-bootstrap';
import _ from 'underscore';
import { humanize, titleize } from 'underscore.string';
import Container from 'components/v2/container';
import Card from 'components/v2/card';
import CircleButton from 'components/v2/buttons/CircleButton';
import PencilIcon from 'components/v2/icons/PencilIcon';
import TrashIcon from 'components/v2/icons/TrashIcon';
import PlusIcon from 'components/v2/icons/PlusIcon';
import CargomaticSelect from 'components/v2/select';
import CargomaticCheckbox from 'components/v2/forms/Checkbox';
import reasonCodes from 'config/reason-codes';
import DataPoller from 'components/ui/DataPoller';
import ShipmentStatusReasonCaptureModal from 'components/ui/tailwind/modals/ShipmentStatusReasonCaptureModal';
import ShipmentSearch from 'components/site/shipment-search';
import CustomerSearch from 'components/site/customer-search';
import ExceptionsActionCreators from 'actions/ExceptionsActionCreators';
import ExceptionStore from 'stores/ExceptionStore';
import { findUsers } from 'utils/UserWebAPIUtils';

import {
  getFilters,
  createFilter,
  updateFilters,
  deleteFilter,
} from 'utils/FiltersWebAPIUtils';
import SessionStore from 'stores/SessionStore';
import { withTranslation } from 'react-i18next';
import { sluggify } from 'utils/localization/utils';
import ExceptionList from './ExceptionList';
import ExceptionResolveModal from './ExceptionResolveModal';
import Badge from '../../v2/Badge';

const EXCEPTION_TYPES = {
  late_pickup: { name: 'late_pickup', localizationSlug: 'LATE_PICKUP' },
  late_delivery: { name: 'late_delivery', localizationSlug: 'LATE_DELIVERY' },
  wait_time_pickup: {
    name: 'wait_time_pickup',
    localizationSlug: 'WAIT_TIME_PICKUP',
  },
  wait_time_delivery: {
    name: 'wait_time_delivery',
    localizationSlug: 'WAIT_TIME_DELIVERY',
  },
  driver_related: {
    name: 'driver_related',
    localizationSlug: 'DRIVER_RELATED',
  },
  change_request: {
    name: 'change_request',
    localizationSlug: 'CHANGE_REQUEST',
  },
  proof_of_delivery: { name: 'pod', localizationSlug: 'POD' },
};
const SHIPMENT_TYPES = [
  { name: 'shorthaul', localizationSlug: 'SHORTHAUL' },
  { name: 'dayrate', localizationSlug: 'DAYRATE' },
  { name: 'longhaul', localizationSlug: 'LONGHAUL' },
  { name: 'drayage', localizationSlug: 'DRAYAGE' },
  { name: 'rail', localizationSlug: 'RAIL' },
];
const QUERY_KEYS = [
  'status',
  'types',
  'sort_by',
  'order',
  'page',
  'shipper',
  'shipment_types',
  'shipperName',
  'resourceId',
  'driver',
  'carrier',
  'severity',
  'categories',
  'master_shipper',
  'excluded_master_shipper',
  'on_hold',
  'assignee',
  'per_page',
];

const SAVED_FILTER_KEYS = [
  'on_hold',
  'assignee',
  'status',
  'types',
  'shipper',
  'shipment_types',
  'driver',
  'carrier',
  'severity',
  'categories',
  'master_shipper',
  'excluded_master_shipper',
];

const emptyArray = [];

const warnings = {
  carrier: 'CURRENTLY_FILTERED_BY_CARRIER_SELECTION',
  shipper: 'CURRENTLY_FILTERED_BY_SHIPPER_SELECTION',
  master_shipper: 'CURRENTLY_FILTERED_BY_MASTER_ACCOUNT_SELECTION',
  excluded_master_shipper:
    'CURRENTLY_FILTERED_BY_EXCLUDED_MASTER_ACCOUNT_SELECTION',
  shipperName: 'CURRENTLY_FILTERED_BY_SHIPPER_NAME_SELECTION_CASE_INSENSITIVE',
  shipment: 'CURRENTLY_FILTERED_BY_SHIPMENT_ID_NAME_SELECTION',
};

function convertUserListToHash(lookup, list = []) {
  return list.reduce((map, id) => {
    map[id] = lookup[id];
    return map;
  }, {});
}

class ShipmentExceptions extends Component {
  constructor(props) {
    super(props);
    const user = SessionStore.getUser();

    this.state = {
      selectedFilter: '',
      toggleUpdate: false,
      toggleCreate: false,
      toggleDelete: false,
      filtersLoaded: false,
      filters: [],
      user,
      exceptions: [],
      status: 'unresolved',
      shipper: [],
      master_shipper: [],
      carrier: [],
      driver: [],
      resourceId: [],
      shipment_types: [
        { name: 'longhaul', localizationSlug: 'LONGHAUL' },
        { name: 'dayrate', localizationSlug: 'DAYRATE' },
        { name: 'shorthaul', localizationSlug: 'SHORTHAUL' },
      ],
      types: ['transition', 'approval', 'capture', 'change', 'quality'],
      excluded_master_shipper: [CEVA_MASTER_ACCOUNT],
      warning: warnings.excluded_master_shipper,
      sort_by: '_id',
      order: 'asc',
      page: 1,
      per_page: 25,
      resolveException: null,
      categories: [],
      steps: [],
      severity: [],
      showSaveFilter: false,
      sharedFilter: true,
      filterName: '',
      meta: {
        shipper: {},
        carrier: {},
        master_shipper: {},
        driver: {},
        excluded_master_shipper: {},
        resourceId: {},
      },
    };
  }

  async componentDidMount() {
    ExceptionStore.addChangeListener(this.onChange.bind(this));
    this.getSavedFilters();
    const response = await this.populateFilters(
      this.state.meta,
      this.getFilters(),
    );

    const { meta, filters } = response;
    this.setState({ meta, ...filters });
  }

  componentDidUpdate(prevProps, prevState) {
    if (_.some(QUERY_KEYS, (key) => prevState[key] !== this.state[key])) {
      this.queryExceptions();
    }
  }

  componentWillUnmount() {
    ExceptionStore.removeChangeListener(this.onChange.bind(this));
  }

  async getSavedFilters() {
    try {
      const { entity: filters } = await getFilters({
        _type: 'shipment-exception',
      });
      this.setState({ filters });
    } catch (e) {
      console.log(e);
    }
  }

  actionsToggled() {
    return (
      this.state.toggleCreate ||
      this.state.toggleUpdate ||
      this.state.toggleDelete
    );
  }

  onFilterNameChange = (e) => {
    const {
      target: { value: filterName },
    } = e;

    this.setState({ filterName });
  };

  onToggleShared = (sharedFilter) => {
    this.setState({ sharedFilter });
  };

  onToggleCreate = () =>
    this.setState(() => ({ toggleCreate: true, filterName: '' }));
  onToggleUpdate = () =>
    this.setState((prevState) => {
      const filter = prevState.filters.find(
        (f) => f._id === prevState.selectedFilter,
      );
      return {
        toggleUpdate: true,
        filterName: filter.name,
      };
    });
  onToggleDelete = () => this.setState(() => ({ toggleDelete: true }));

  onApplyFilter = async (filter) => {
    const { _id: selectedFilter } = filter;

    if (!selectedFilter) {
      this.setState({ selectedFilter }, () => this.queryExceptions());
      return;
    }

    const { meta, filters } = await this.populateFilters(
      this.state.meta,
      filter.model,
    );

    this.setState(() => {
      try {
        const { name: filterName, shared: sharedFilter } = filter;
        const update = {
          filterName,
          sharedFilter,
          ...filters,
          sort_by: '_id',
          order: 'asc',
          page: 1,
          meta,
        };
        return update;
      } catch (e) {
        console.error(e);
      }
    }, this.queryExceptions());
    this.setState(
      {
        ...filter.model,
        filterName: filter.name,
        sharedFilter: filter.shared,
        selectedFilter,
      },
      this.queryExceptions(),
    );
  };

  onUpdateFilter = async () => {
    const model = this.getFiltersForSave();
    const {
      filterName: name,
      sharedFilter: shared,
      selectedFilter,
    } = this.state;

    try {
      const index = this.state.filters.findIndex(
        (f) => f._id === selectedFilter,
      );
      const filter = {
        ...this.state.filters[index],
        id: selectedFilter,
        name,
        shared,
        model,
      };

      const { entity: updatedFilter } = await updateFilters(filter);

      this.setState((prevState) => ({
        filters: [
          ...prevState.filters.slice(0, index),
          updatedFilter,
          ...prevState.filters.slice(index + 1),
        ],
        selectedFilter: updatedFilter._id,
      }));
      this.onHideFilterActions();
    } catch (e) {
      console.error(e);
    }
  };

  onCreateFilter = async () => {
    const model = this.getFiltersForSave();
    const { filterName: name, sharedFilter: shared } = this.state;

    try {
      const { entity: newFilter } = await createFilter({
        name,
        shared,
        model,
        create_id: SessionStore.getUser()._id,
        _type: 'shipment-exception',
        view: 'shipment-exceptions',
      });

      this.setState((prevState) => ({
        filters: [...prevState.filters, newFilter],
        selectedFilter: newFilter._id,
      }));
      this.onHideFilterActions();
    } catch (e) {
      console.error(e);
    }
  };

  onDeleteFilter = async () => {
    try {
      deleteFilter(this.state.selectedFilter);
      this.onHideFilterActions();
      this.setState((prevState) => {
        const index = prevState.filters.findIndex(
          (f) => f._id === this.state.selectedFilter,
        );
        return {
          filters: [
            ...prevState.filters.slice(0, index),
            ...prevState.filters.slice(index + 1),
          ],
          selectedFilter: '',
        };
      });
    } catch (e) {
      console.error(e);
    }
  };

  onHideFilterActions = () => {
    this.setState({
      toggleCreate: false,
      toggleUpdate: false,
      toggleDelete: false,
    });
  };

  onSaveFilter = () => {
    if (this.state.toggleUpdate) {
      this.onUpdateFilter();
    } else if (this.state.toggleCreate) {
      this.onCreateFilter();
    } else if (this.state.toggleDelete) {
      this.onDeleteFilter();
    }
  };

  onInitiateResolution(exception) {
    this.mergeState({ resolveException: exception });
  }

  onCancelResolution() {
    this.mergeState({ resolveException: null }, () => {
      this.queryExceptions();
    });
  }

  onPaginationSelect(eventKey) {
    this.mergeState({ page: eventKey });
  }

  onChange = () => {
    // If new list has fewer pages than the page we were on, reset to page 1
    const totalPages = ExceptionStore.getTotalPages();
    const page = totalPages < this.state.page ? 1 : this.state.page;
    this.mergeState({
      exceptions: _.sortBy(ExceptionStore.getAllExceptions(), '_id'),
      page,
    });
  };

  onResolve(id, message, meta) {
    ExceptionsActionCreators.addResolution(id, message, meta);
    this.queryExceptions();
  }

  getHumanizedCategories() {
    const { t } = this.props;
    return this.state.categories.map((category) => {
      if (category === 'pod') {
        return 'PROOF_OF_DELIVERY';
      }
      return humanize(category);
    });
  }

  getHumanizedShipmentTypes() {
    const { t } = this.props;
    return this.state.shipment_types.map((shipment_type) => {
      return humanize(t(shipment_type.localizationSlug));
    });
  }

  mergeState(newState) {
    this.setState((prevState) => {
      return {
        ...prevState,
        ...newState,
      };
    });
  }

  getFiltersForSave() {
    const whitelisted = _.pick(this.state, SAVED_FILTER_KEYS);
    return _.omit(whitelisted, (value) => !value);
  }

  getFilters() {
    const whitelisted = _.pick(this.state, QUERY_KEYS);
    whitelisted.shipment_types = whitelisted.shipment_types.map(
      (type) => type.name,
    );
    return _.omit(whitelisted, (value) => !value);
  }

  collectUsers(filters = {}) {
    const users = [];
    if (filters.shipper) {
      users.push(...filters.shipper);
    }

    if (filters.carrier) {
      users.push(...filters.carrier);
    }

    if (filters.driver) {
      users.push(...filters.carrier);
    }

    if (filters.master_shipper) {
      users.push(...filters.master_shipper);
    }

    if (filters.excluded_master_shipper) {
      users.push(...filters.excluded_master_shipper);
    }
    return users;
  }

  populateFilters = async (incomingMeta, filters) => {
    const userIds = this.collectUsers(filters);

    if (userIds.length === 0) {
      return {
        meta: incomingMeta,
        filters,
      };
    }
    const {
      entity: { items: users },
    } = await findUsers({ _id: userIds });

    const userMap = users.reduce((runningMap, user) => {
      const { _id, id } = user;
      const name = user.company_name || user.name;
      return {
        ...runningMap,
        [user._id]: { _id, id, name },
      };
    }, {});

    const meta = {
      ...incomingMeta,
      shipper: convertUserListToHash(userMap, filters.shipper),
      carrier: convertUserListToHash(userMap, filters.carrier),
      driver: convertUserListToHash(userMap, filters.driver),
      master_shipper: convertUserListToHash(userMap, filters.master_shipper),
      excluded_master_shipper: convertUserListToHash(
        userMap,
        filters.excluded_master_shipper,
      ),
    };

    filters.shipment_types = (filters?.shipment_types || []).map((type) => {
      return { name: type, localizationSlug: type.toUpperCase() };
    });

    return {
      filters,
      meta,
    };
  };

  queryExceptions = () => {
    const query = this.getFilters();

    if (this.state.user) {
      if (this.state.user._type === 'Admin') {
        ExceptionsActionCreators.getExceptions(query);
      }
      if (this.state.user._type === 'Shipper') {
        ExceptionsActionCreators.getShipperExceptions(query);
      }
    }
  };

  handleRemoveShipment = (id) => () => {
    this.setState((prevState) => {
      const index = prevState.resourceId.findIndex((s) => s === id);
      const meta = {
        ...prevState.meta,
        resourceId: {
          ...prevState.meta.resourceId,
        },
      };
      delete meta.resourceId[id];
      const resourceId = [
        ...prevState.resourceId.slice(0, index),
        ...prevState.resourceId.slice(index + 1),
      ];

      return {
        resourceId,
        meta,
      };
    });
  };

  handleRemoveShipper = (id) => () => {
    this.setState((prevState) => {
      const index = prevState.shipper.findIndex((s) => s === id);
      const meta = {
        ...prevState.meta,
        shipper: {
          ...prevState.meta.shipper,
        },
      };
      delete meta.shipper[id];
      const shipper = [
        ...prevState.shipper.slice(0, index),
        ...prevState.shipper.slice(index + 1),
      ];

      return {
        shipper,
        meta,
      };
    });
  };

  handleRemoveMasterShipper = (id) => () => {
    this.setState((prevState) => {
      const index = prevState.master_shipper.findIndex((s) => s === id);
      const meta = {
        ...prevState.meta,
        master_shipper: {
          ...prevState.meta.master_shipper,
        },
      };
      delete meta.master_shipper[id];
      return {
        master_shipper: [
          ...prevState.master_shipper.slice(0, index),
          ...prevState.master_shipper.slice(index + 1),
        ],
        meta,
      };
    });
  };

  handleRemoveExcludedMasterShipper = (id) => () => {
    this.setState((prevState) => {
      const index = prevState.excluded_master_shipper.findIndex(
        (s) => s === id,
      );
      const meta = {
        ...prevState.meta,
        excluded_master_shipper: {
          ...prevState.meta.excluded_master_shipper,
        },
      };
      delete meta.excluded_master_shipper[id];
      const excluded_master_shipper = [
        ...prevState.excluded_master_shipper.slice(0, index),
        ...prevState.excluded_master_shipper.slice(index + 1),
      ];
      let warning = prevState.warning;

      if (
        excluded_master_shipper.length === 0 &&
        warning === warnings.excluded_master_shipper
      ) {
        warning = '';
      }
      return {
        excluded_master_shipper: [
          ...prevState.excluded_master_shipper.slice(0, index),
          ...prevState.excluded_master_shipper.slice(index + 1),
        ],
        meta,
        warning,
      };
    });
  };

  handleRemoveCarrier = (id) => () => {
    this.setState((prevState) => {
      const index = prevState.carrier.findIndex((s) => s === id);
      const meta = {
        ...prevState.meta,
        carrier: {
          ...prevState.meta.carrier,
        },
      };
      delete meta.carrier[id];
      return {
        carrier: [
          ...prevState.carrier.slice(0, index),
          ...prevState.carrier.slice(index + 1),
        ],
        meta,
      };
    });
  };

  handleRemoveDriver = (id) => () => {
    this.setState((prevState) => {
      const index = prevState.driver.findIndex((s) => s === id);
      const meta = {
        ...prevState.meta,
        driver: {
          ...prevState.meta.driver,
        },
      };
      delete meta.driver[id];
      return {
        driver: [
          ...prevState.driver.slice(0, index),
          ...prevState.driver.slice(index + 1),
        ],
        meta,
      };
    });
  };

  filterByCustomer(value) {
    this.setState(
      (prevState) => {
        const shipper = _.uniq((prevState.shipper || []).concat(value.value));
        const meta = {
          ...prevState.meta,
          shipper: {
            ...prevState.meta.shipper,
            [value.value]: value,
          },
        };
        return {
          shipper,
          meta,
        };
      },
      () => this.queryExceptions(),
    );
  }

  filterByExcludedMasterShipper(value) {
    this.setState(
      (prevState) => {
        const meta = {
          ...prevState.meta,
          excluded_master_shipper: {
            ...prevState.meta.excluded_master_shipper,
            [value.value]: value,
          },
        };

        const excluded_master_shipper = _.uniq([
          ...prevState.excluded_master_shipper,
          value.value,
        ]);

        return {
          excluded_master_shipper,
          meta,
        };
      },
      () => this.queryExceptions(),
    );
  }

  filterByMasterShipper(value) {
    this.setState(
      (prevState) => {
        const meta = {
          ...prevState.meta,
          master_shipper: {
            ...prevState.meta.master_shipper,
            [value.value]: value,
          },
        };

        return {
          master_shipper: _.uniq([...prevState.master_shipper, value.value]),
          meta,
        };
      },
      () => this.queryExceptions(),
    );
  }

  filterByCategory(category, checked) {
    this.setState(
      (prevState) => {
        let categories = prevState.categories;
        if (checked) {
          categories.push(category);
        } else {
          categories = _.without(categories, category);
        }
        return {
          categories,
        };
      },
      () => this.queryExceptions(),
    );
  }

  filterByShipmentTypes(shipment_type, checked) {
    this.setState((prevState) => {
      let shipment_types = [...prevState.shipment_types];
      if (checked) {
        shipment_types.push(shipment_type);
      } else {
        shipment_types = _.filter(
          shipment_types,
          (type) => type.name !== shipment_type.name,
        );
      }
      return {
        shipment_types,
      };
    });
  }

  filterByDriver(value) {
    this.setState(
      (prevState) => {
        const driver = _.uniq((prevState.driver || []).concat(value.value));
        const meta = {
          ...prevState.meta,
          driver: {
            ...prevState.meta.driver,
            [value.value]: value,
          },
        };
        return {
          driver,
          meta,
        };
      },
      () => this.queryExceptions(),
    );
  }

  filterByCarrier(value) {
    this.setState(
      (prevState) => {
        const carrier = _.uniq((prevState.carrier || []).concat(value.value));
        const meta = {
          ...prevState.meta,
          carrier: {
            ...prevState.meta.carrier,
            [value.value]: value,
          },
        };
        return {
          carrier,
          meta,
        };
      },
      () => this.queryExceptions(),
    );
  }

  filterByShipment(shipment) {
    const { _id: id, shipment_id } = shipment;
    this.setState(
      (prevState) => {
        const meta = {
          ...prevState.meta,
          resourceId: {
            ...prevState.meta.resourceId,
          },
        };
        meta.resourceId[id] = { id, shipment_id };

        const resourceId = _.uniq([...prevState.resourceId, id]);

        return {
          resourceId,
          meta,
        };
      },
      () => this.queryExceptions(),
    );
  }

  showModal(types = emptyArray) {
    if (!this.state.resolveException) {
      return false;
    }

    return types.includes(this.state.resolveException.type);
  }

  renderExceptionTypePopup() {
    const { t } = this.props;
    return (
      <Popover id="exception-types-popup">
        <ul className="filter-dropdown-wrapper">
          {_.keys(EXCEPTION_TYPES).map((type) => {
            return (
              <li key={type}>
                <Checkbox
                  onChange={(e) =>
                    this.filterByCategory(
                      EXCEPTION_TYPES[type].name,
                      e.target.checked,
                    )
                  }
                  checked={
                    this.state.categories.indexOf(EXCEPTION_TYPES[type].name) >
                    -1
                  }
                >
                  {humanize(t(EXCEPTION_TYPES[type].localizationSlug))}
                </Checkbox>
              </li>
            );
          })}
        </ul>
      </Popover>
    );
  }

  renderShipmentTypePopup() {
    const { t } = this.props;
    return (
      <Popover id="shipment-types-popup">
        <ul className="filter-dropdown-wrapper">
          {SHIPMENT_TYPES.map((type, i) => {
            return (
              <li key={i}>
                <Checkbox
                  checked={
                    this.state.shipment_types
                      .map((shipmentType) => shipmentType.name)
                      .indexOf(type.name) > -1
                  }
                  onChange={(e) =>
                    this.filterByShipmentTypes(type, e.target.checked)
                  }
                >
                  {humanize(t(type.localizationSlug))}
                </Checkbox>
              </li>
            );
          })}
        </ul>
      </Popover>
    );
  }
  renderFilterByStatus() {
    const { t } = this.props;
    return (
      <div className="dropdown-filter-wrapper mb-4">
        <label className="form-label block mb-2">
          {t('FILTER_BY_EXCEPTION_TYPE')}
        </label>
        <OverlayTrigger
          className="dropdown-filter-wrapper"
          rootClose
          trigger="click"
          placement="bottom"
          overlay={this.renderExceptionTypePopup()}
        >
          <div>
            <Button
              bsStyle={this.state.categories.length > 0 ? 'danger' : 'default'}
              style={{ outline: 'none' }}
              bsSize="small"
            >
              {this.state.categories != null ? (
                <div className="flex flex-wrap items-center truncate -mx-2">
                  <div className="flex space-x-1 px-2">
                    <span className="text-sm">{t('EXCEPTION_TYPE')}</span>
                    <Glyphicon
                      glyph="chevron-down"
                      className="button-down-glyph"
                    />
                  </div>
                  {this.state.categories.length > 0 &&
                    this.getHumanizedCategories().map((category) => (
                      <span className="text-sm px-2">
                        {humanize(t(sluggify(category)))}
                      </span>
                    ))}
                </div>
              ) : (
                t('SEVERITY')
              )}
            </Button>
          </div>
        </OverlayTrigger>
      </div>
    );
  }

  renderFilterByShipmentType() {
    const { t } = this.props;
    return (
      <div className="dropdown-filter-wrapper mb-4">
        <label className="form-label block mb-2">
          {t('FILTER_BY_SHIPMENT_TYPE')}
        </label>
        <OverlayTrigger
          className="dropdown-filter-wrapper"
          rootClose
          trigger="click"
          placement="bottom"
          overlay={this.renderShipmentTypePopup()}
        >
          <div>
            <Button
              bsStyle={
                this.state.shipment_types.length > 0 ? 'danger' : 'default'
              }
              style={{ outline: 'none' }}
              bsSize="small"
            >
              <div className="flex flex-wrap items-center truncate -mx-2">
                <div className="flex space-x-1 px-2">
                  <span className="text-sm">{t('SHIPMENT_TYPE')}</span>
                  <Glyphicon
                    glyph="chevron-down"
                    className="button-down-glyph"
                  />
                </div>
                {this.state.shipment_types.length > 0 &&
                  this.getHumanizedShipmentTypes().map((shipment_type) => (
                    <span className="text-sm px-2">{shipment_type}</span>
                  ))}
              </div>
            </Button>
          </div>
        </OverlayTrigger>
      </div>
    );
  }

  renderCarrierFilter() {
    const { t } = this.props;
    const carrierMeta = this.state.meta.carrier;
    return (
      <div className="mb-4">
        <label className="form-label block mb-2">
          {t('FILTER_BY_CARRIER')}
        </label>
        <CustomerSearch
          clearOnSelect
          type="carriers"
          onSelect={(_item, value) => this.filterByCarrier(value)}
          placeholder={t('SEARCH_FOR_CARRIERS')}
        />
        {this.state.carrier.length > 0 && (
          <div className="flex flex-wrap -mx-2 mt-2 mb-4">
            {this.state.carrier.map((carrier) => (
              <div className="px-2 mb-2" key={carrier}>
                <Badge
                  color="blue"
                  withRemove
                  onRemove={this.handleRemoveCarrier(carrier)}
                >
                  {(carrierMeta[carrier] || {}).name || carrier}
                </Badge>
              </div>
            ))}
          </div>
        )}
      </div>
    );
  }

  renderDriverFilter() {
    const driverMeta = this.state.meta.driver;
    const { t } = this.props;
    return (
      <div className="mb-4">
        <label className="form-label block mb-2">{t('FILTER_BY_DRIVER')}</label>
        <CustomerSearch
          clearOnSelect
          type="drivers"
          onSelect={(_item, value) => this.filterByDriver(value)}
          placeholder={t('SEARCH_FOR_DRIVER')}
        />
        {this.state.driver.length > 0 && (
          <div className="flex flex-wrap -mx-2 mt-2 mb-4">
            {this.state.driver.map((driver) => (
              <div className="px-2 mb-2" key={driver}>
                <Badge
                  color="blue"
                  withRemove
                  onRemove={this.handleRemoveDriver(driver)}
                >
                  {(driverMeta[driver] || {}).name || driver}
                </Badge>
              </div>
            ))}
          </div>
        )}
      </div>
    );
  }

  renderExcludedMasterAccountFilter() {
    const excludedMasterShipperMeta = this.state.meta.excluded_master_shipper;
    const { t } = this.props;
    return (
      <div className="mb-4">
        <label className="form-label block mb-2">
          {t('EXCLUDE_MASTER_ACCOUNT')}
        </label>
        <CustomerSearch
          clearOnSelect
          type="master_shippers"
          onSelect={(_item, value) => this.filterByExcludedMasterShipper(value)}
          placeholder={t('SEARCH_FOR_MASTER_ACCOUNT')}
        />
        {this.state.excluded_master_shipper.length > 0 && (
          <div className="flex flex-wrap -mx-2 mt-2 mb-4">
            {this.state.excluded_master_shipper.map((shipper) => (
              <div className="px-2 mb-2" key={shipper}>
                <Badge
                  color="green"
                  withRemove
                  onRemove={this.handleRemoveExcludedMasterShipper(shipper)}
                >
                  {(excludedMasterShipperMeta[shipper] || {}).name || shipper}
                </Badge>
              </div>
            ))}
          </div>
        )}
      </div>
    );
  }

  renderMasterAccountFilter() {
    const masterShipperMeta = this.state.meta.master_shipper;
    const { t } = this.props;
    return (
      <div className="mb-4">
        <label className="form-label block mb-2">
          {t('FILTER_BY_MASTER_ACCOUNT')}
        </label>
        <CustomerSearch
          clearOnSelect
          type="master_shippers"
          onSelect={(_item, value) => this.filterByMasterShipper(value)}
          placeholder={t('SEARCH_FOR_MASTER_ACCOUNT')}
        />
        {this.state.master_shipper.length > 0 && (
          <div className="flex flex-wrap -mx-2 mt-2 mb-4">
            {this.state.master_shipper.map((shipper) => (
              <div className="px-2 mb-2" key={shipper}>
                <Badge
                  color="green"
                  withRemove
                  onRemove={this.handleRemoveMasterShipper(shipper)}
                >
                  {(masterShipperMeta[shipper] || {}).name || shipper}
                </Badge>
              </div>
            ))}
          </div>
        )}
      </div>
    );
  }

  renderCustomerFilter = () => {
    const shipperMeta = this.state.meta.shipper;
    const { t } = this.props;
    return (
      <div className="mb-4">
        <label className="form-label block mb-2">
          {t('FILTER_BY_SHIPPER')}
        </label>
        <CustomerSearch
          clearOnSelect
          type="shippers"
          onSelect={(_item, value) => this.filterByCustomer(value)}
          placeholder={t('SEARCH_FOR_SHIPPER_PLACEHOLDER')}
        />
        {this.state.shipper.length > 0 && (
          <div className="flex flex-wrap -mx-2 mt-2 mb-4">
            {this.state.shipper.map((shipper) => (
              <div className="px-2 mb-2" key={shipper}>
                <Badge
                  color="green"
                  withRemove
                  onRemove={this.handleRemoveShipper(shipper)}
                >
                  {(shipperMeta[shipper] || {}).name || shipper}
                </Badge>
              </div>
            ))}
          </div>
        )}
      </div>
    );
  };

  renderShipmentFilter = () => {
    const shipmentMeta = this.state.meta.resourceId;
    const { t } = this.props;
    return (
      <div className="mb-4">
        <label className="form-label block mb-2">
          {t('FILTER_BY_SHIPMENT_ID')}
        </label>
        <ShipmentSearch
          onSelect={(_shipment_id, shipment) => this.filterByShipment(shipment)}
        />
        {this.state.resourceId.length > 0 && (
          <div className="flex flex-wrap -mx-2 mt-2 mb-4">
            {this.state.resourceId.map((shipment) => (
              <div className="px-2 mb-2" key={shipment}>
                <Badge
                  color="brand"
                  withRemove
                  onRemove={this.handleRemoveShipment(shipment)}
                >
                  {(shipmentMeta[shipment] || {}).shipment_id || shipment}
                </Badge>
              </div>
            ))}
          </div>
        )}
      </div>
    );
  };

  renderSavedFilters() {
    const hasFilters = this.state.filters.length > 0;
    const actionsToggled = this.actionsToggled();
    const { t } = this.props;
    return (
      <div className="flex items-end flex-wrap sm:-mx-8">
        {!actionsToggled && hasFilters && (
          <div className="sm:px-8">
            <div className="flex items-center space-x-2 mb-1">
              <label className="form-label">{t('APPLY_FILTER')}</label>
            </div>
            <CargomaticSelect
              hideLabel
              className="w-72"
              valueKey="_id"
              textKey="name"
              items={this.state.filters}
              value={this.state.selectedFilter}
              onSelect={this.onApplyFilter}
            />
          </div>
        )}
        {!actionsToggled && (
          <div className="sm:px-8">
            <label className="mb-1 form-label">
              {titleize(t('ACTION', { count: 2 }))}
            </label>
            <div className="flex items-center -mx-1">
              {hasFilters && [
                <div className="px-1" key="toggleUpdate">
                  <CircleButton
                    onClick={this.onToggleUpdate}
                    color="blue"
                    disabled={!this.state.selectedFilter}
                    title={t('UPDATE_FILTER')}
                  >
                    <PencilIcon />
                  </CircleButton>
                </div>,
                <div className="px-1" key="toggleDelete">
                  <CircleButton
                    onClick={this.onToggleDelete}
                    disabled={!this.state.selectedFilter}
                    color="red"
                    title={t('DELETE_FILTER')}
                  >
                    <TrashIcon />
                  </CircleButton>
                </div>,
              ]}
              <div className="px-1">
                <CircleButton
                  onClick={this.onToggleCreate}
                  color="brand"
                  size="small"
                  title={t('CREATE_NEW')}
                >
                  <PlusIcon />
                </CircleButton>
              </div>
            </div>
          </div>
        )}
        {this.state.toggleDelete && (
          <div className="px-8 flex justify-start items-center -mx-4">
            <div className="px-4">
              <label className="mb-2">{t('CONFIRM_DELETION_OF_FILTER')}</label>
              <div className="mt-3 sm:mt-0 flex items-center -mx-3 justify-end sm:justify-start">
                <div className="px-3">
                  <button
                    type="button"
                    className="button button-red"
                    onClick={this.onSaveFilter}
                  >
                    {titleize(t('CONFIRM'))}
                  </button>
                </div>
                <div className="px-3">
                  <button
                    type="button"
                    className=""
                    onClick={this.onHideFilterActions}
                  >
                    {t('CANCEL')}
                  </button>
                </div>
              </div>
            </div>
          </div>
        )}
        {(this.state.toggleUpdate || this.state.toggleCreate) && (
          <div className="flex justify-start items-center px-8">
            <div className="flex flex-wrap items-center -mx-3">
              <div className="px-3 flex items-center -mx-3">
                <div className="px-3">
                  <input
                    type="text"
                    className="block form-field w-64"
                    onChange={this.onFilterNameChange}
                    placeholder={t('FILTER_NAME')}
                    value={this.state.filterName}
                  />
                </div>
                <div className="px-3">
                  <CargomaticCheckbox
                    id="sharedFilter"
                    checked={this.state.sharedFilter}
                    onChange={this.onToggleShared}
                  >
                    {t('SHARED_QUES')}
                  </CargomaticCheckbox>
                </div>
              </div>
              <div className="mt-3 sm:mt-0 flex items-center -mx-1 justify-end sm:justify-start">
                <div className="px-1">
                  <button
                    type="button"
                    className="button button-green"
                    onClick={this.onSaveFilter}
                  >
                    {titleize(t('SAVE'))}
                  </button>
                </div>
                <div className="px-3">
                  <button
                    type="button"
                    className=""
                    onClick={this.onHideFilterActions}
                  >
                    {t('CANCEL')}
                  </button>
                </div>
              </div>
            </div>
          </div>
        )}
      </div>
    );
  }

  render() {
    const { t } = this.props;
    if (!this.state.user) return <div>{t('LOADING')}</div>;

    return (
      <Container className="py-12">
        <div>
          <DataPoller action={() => this.queryExceptions()} />
          <div className="">
            <Card
              className="mb-6"
              grayFooter
              header={() => (
                <h3 className="my-0 mb-4 sm:mb-0">
                  {t('SHIPMENT_EXCEPTIONS')}
                </h3>
              )}
              footer={() => this.renderSavedFilters()}
            >
              <div className="">
                <div className="flex flex-wrap -mx-4">
                  <div className="sm:w-1/2 lg:w-1/4 w-full px-4">
                    {this.renderCustomerFilter()}
                  </div>
                  <div className="sm:w-1/2 lg:w-1/4 w-full px-4">
                    {this.renderShipmentFilter()}
                  </div>
                  <div className="sm:w-1/2 lg:w-1/4 w-full px-4">
                    {this.renderCarrierFilter()}
                  </div>
                  <div className="sm:w-1/2 lg:w-1/4 w-full px-4">
                    {this.renderDriverFilter()}
                  </div>
                </div>
                <div className="flex flex-wrap -mx-4">
                  <div className="sm:w-1/2 lg:w-1/4 w-full px-4">
                    {this.renderFilterByStatus()}
                  </div>
                  <div className="sm:w-1/2 lg:w-1/4 w-full px-4">
                    {this.renderMasterAccountFilter()}
                  </div>
                  <div className="sm:w-1/2 lg:w-1/4 w-full px-4">
                    {this.renderExcludedMasterAccountFilter()}
                  </div>
                  <div className="sm:w-1/2 lg:w-1/4 w-full px-4">
                    {this.renderFilterByShipmentType()}
                  </div>
                </div>
              </div>
            </Card>
            <div className="w-full">
              <ExceptionList
                page={this.state.page}
                totalCount={ExceptionStore.getTotalCount()}
                totalPages={ExceptionStore.getTotalPages()}
                perPage={this.state.per_page}
                exceptions={this.state.exceptions}
                onInitiateResolution={this.onInitiateResolution.bind(this)}
                userType={this.state.user._type}
                status={this.state.status}
                onPaginationSelect={(eventKey) =>
                  this.onPaginationSelect(eventKey)
                }
              />
            </div>
            {this.showModal(['transition', 'approval', 'quality']) && (
              <ExceptionResolveModal
                show={this.showModal(['transition', 'approval', 'quality'])}
                exception={this.state.resolveException}
                onCancel={this.onCancelResolution.bind(this)}
                onResolve={this.onResolve.bind(this)}
              />
            )}

            <ShipmentStatusReasonCaptureModal
              show={this.showModal(['capture'])}
              reasons={reasonCodes}
              onResolve={this.onResolve.bind(this)}
              exception={this.state.resolveException}
              onCancel={this.onCancelResolution.bind(this)}
            />
          </div>
        </div>
      </Container>
    );
  }
}

export default withTranslation()(ShipmentExceptions);
